/**
 * Deterministic maths: the transcendental functions game logic uses, the same to the
 * last bit on every JavaScript engine.
 *
 * ECMAScript pins `+ - * /` and `Math.sqrt` to IEEE 754 (correctly rounded), but not
 * `Math.sin/cos/tan/asin/acos/atan/atan2/exp/log/pow` or `**`: engines differ in the
 * last bit (Node 22 vs Chrome: 3–19% of results, `npm run probe:float`). A match is a
 * deterministic simulation shared by P2P peers, replays and auto-resolve, so logic code
 * uses these instead (tests/dmath.test.ts forbids the native ones in core / ai / match).
 *
 * Ports of fdlibm (Sun Microsystems, 1993: "Developed at SunPro, a Sun Microsystems,
 * Inc. business. Permission to use, copy, modify, and distribute this software is freely
 * granted, provided that this notice is preserved.") built only from the exact operations
 * above, so the results are identical everywhere and within about 1 ulp of the true value.
 * `dtan` is dsin/dcos and `dpow` is dexp(y·dlog(x)) (integer exponents by squaring):
 * a few ulp, still deterministic.
 */

// ---- IEEE double words ----------------------------------------------------------------

const F = new Float64Array(1);
const W = new Int32Array(F.buffer);
const HI = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1 ? 1 : 0;   // little-endian: high word is [1]
const LO = 1 - HI;

function hi(x: number): number { F[0] = x; return W[HI]; }
function lo(x: number): number { F[0] = x; return W[LO]; }
function words(h: number, l: number): number { W[HI] = h; W[LO] = l; return F[0]; }
function withHi(x: number, h: number): number { F[0] = x; W[HI] = h; return F[0]; }
function withLoZero(x: number): number { F[0] = x; W[LO] = 0; return F[0]; }

// ---- argument reduction (e_rem_pio2.c, the |x| < 2^20·π/2 path) ---------------------

const invpio2 = 6.36619772367581382433e-01;
const pio2_1 = 1.57079632673412561417e+00;
const pio2_1t = 6.07710050650619224932e-11;
const pio2_2 = 6.07710050630396597660e-11;
const pio2_2t = 2.02226624879595063154e-21;
const pio2_3 = 2.02226624871116645580e-21;
const pio2_3t = 8.47842766036889956997e-32;

let y0 = 0, y1 = 0;   // the reduced argument x - n·π/2 = y0 + y1

/** n such that x - n·π/2 = y0 + y1 with |y0 + y1| ≤ π/4 (ish). */
function remPio2(x: number): number {
  const hx = hi(x);
  const ix = hx & 0x7fffffff;
  const t0 = Math.abs(x);
  const n = (t0 * invpio2 + 0.5) | 0;
  const fn = n;
  let r = t0 - fn * pio2_1;
  let w = fn * pio2_1t;
  const j = ix >> 20;
  y0 = r - w;
  let i = j - ((hi(y0) >> 20) & 0x7ff);
  if (i > 16) {
    let t = r;
    w = fn * pio2_2;
    r = t - w;
    w = fn * pio2_2t - ((t - r) - w);
    y0 = r - w;
    i = j - ((hi(y0) >> 20) & 0x7ff);
    if (i > 49) {
      t = r;
      w = fn * pio2_3;
      r = t - w;
      w = fn * pio2_3t - ((t - r) - w);
      y0 = r - w;
    }
  }
  y1 = (r - y0) - w;
  if (hx < 0) { y0 = -y0; y1 = -y1; return -n; }
  return n;
}

// ---- kernels on [-π/4, π/4] (k_sin.c, k_cos.c) ------------------------------------

const S1 = -1.66666666666666324348e-01;
const S2 = 8.33333333332248946124e-03;
const S3 = -1.98412698298579493134e-04;
const S4 = 2.75573137070700676789e-06;
const S5 = -2.50507602534068634195e-08;
const S6 = 1.58969099521155010221e-10;

function kSin(x: number, y: number, iy: number): number {
  const z = x * x;
  const v = z * x;
  const r = S2 + z * (S3 + z * (S4 + z * (S5 + z * S6)));
  if (iy === 0) return x + v * (S1 + z * r);
  return x - ((z * (0.5 * y - v * r) - y) - v * S1);
}

const C1 = 4.16666666666666019037e-02;
const C2 = -1.38888888888741095749e-03;
const C3 = 2.48015872894767294178e-05;
const C4 = -2.75573143513906633035e-07;
const C5 = 2.08757232129817482790e-09;
const C6 = -1.13596475577881948265e-11;

function kCos(x: number, y: number): number {
  const ix = hi(x) & 0x7fffffff;
  if (ix < 0x3e400000 && (x | 0) === 0) return 1;
  const z = x * x;
  const r = z * (C1 + z * (C2 + z * (C3 + z * (C4 + z * (C5 + z * C6)))));
  if (ix < 0x3fd33333) return 1 - (0.5 * z - (z * r - x * y));
  const qx = ix > 0x3fe90000 ? 0.28125 : words(ix - 0x00200000, 0);
  const hz = 0.5 * z - qx;
  const a = 1 - qx;
  return a - (hz - (z * r - x * y));
}

/** Beyond 2^20·π/2 the medium reduction loses accuracy; game logic never gets there. */
const REDUCE_LIMIT = 0x413921fb;

export function dsin(x: number): number {
  const ix = hi(x) & 0x7fffffff;
  if (ix <= 0x3fe921fb) {
    if (ix < 0x3e400000) return x;
    return kSin(x, 0, 0);
  }
  if (ix >= 0x7ff00000) return NaN;
  if (ix > REDUCE_LIMIT) throw new RangeError(`dsin: argument out of range (${x})`);
  switch (remPio2(x) & 3) {
    case 0: return kSin(y0, y1, 1);
    case 1: return kCos(y0, y1);
    case 2: return -kSin(y0, y1, 1);
    default: return -kCos(y0, y1);
  }
}

export function dcos(x: number): number {
  const ix = hi(x) & 0x7fffffff;
  if (ix <= 0x3fe921fb) return kCos(x, 0);
  if (ix >= 0x7ff00000) return NaN;
  if (ix > REDUCE_LIMIT) throw new RangeError(`dcos: argument out of range (${x})`);
  switch (remPio2(x) & 3) {
    case 0: return kCos(y0, y1);
    case 1: return -kSin(y0, y1, 1);
    case 2: return -kCos(y0, y1);
    default: return kSin(y0, y1, 1);
  }
}

/** tan as dsin/dcos: a few ulp, deterministic. */
export function dtan(x: number): number { return dsin(x) / dcos(x); }

// ---- inverse sine / cosine (e_asin.c, e_acos.c) ---------------------------------

const pi = 3.14159265358979311600e+00;
const pio2_hi = 1.57079632679489655800e+00;
const pio2_lo = 6.12323399573676603587e-17;
const pio4_hi = 7.85398163397448278999e-01;
const pS0 = 1.66666666666666657415e-01;
const pS1 = -3.25565818622400915405e-01;
const pS2 = 2.01212532134862925881e-01;
const pS3 = -4.00555345006794114027e-02;
const pS4 = 7.91534994289814532176e-04;
const pS5 = 3.47933107596021167570e-05;
const qS1 = -2.40339491173441421878e+00;
const qS2 = 2.02094576023350569471e+00;
const qS3 = -6.88283971605453293030e-01;
const qS4 = 7.70381505559019352791e-02;

function pOf(t: number): number { return t * (pS0 + t * (pS1 + t * (pS2 + t * (pS3 + t * (pS4 + t * pS5))))); }
function qOf(t: number): number { return 1 + t * (qS1 + t * (qS2 + t * (qS3 + t * qS4))); }

export function dasin(x: number): number {
  const hx = hi(x);
  const ix = hx & 0x7fffffff;
  if (ix >= 0x3ff00000) {
    if (((ix - 0x3ff00000) | lo(x)) === 0) return x * pio2_hi + x * pio2_lo;   // |x| = 1
    return NaN;
  }
  if (ix < 0x3fe00000) {
    if (ix < 0x3e400000) return x;
    const t = x * x;
    return x + x * (pOf(t) / qOf(t));
  }
  const w0 = 1 - Math.abs(x);
  let t = w0 * 0.5;
  let p = pOf(t);
  let q = qOf(t);
  const s = Math.sqrt(t);
  if (ix >= 0x3fef3333) {
    t = pio2_hi - (2.0 * (s + s * (p / q)) - pio2_lo);
  } else {
    const w = withLoZero(s);
    const c = (t - w * w) / (s + w);
    const r = p / q;
    p = 2.0 * s * r - (pio2_lo - 2.0 * c);
    q = pio4_hi - 2.0 * w;
    t = pio4_hi - (p - q);
  }
  return hx > 0 ? t : -t;
}

export function dacos(x: number): number {
  const hx = hi(x);
  const ix = hx & 0x7fffffff;
  if (ix >= 0x3ff00000) {
    if (((ix - 0x3ff00000) | lo(x)) === 0) return hx > 0 ? 0.0 : pi + 2.0 * pio2_lo;   // |x| = 1
    return NaN;
  }
  if (ix < 0x3fe00000) {
    if (ix <= 0x3c600000) return pio2_hi + pio2_lo;
    const z = x * x;
    const r = pOf(z) / qOf(z);
    return pio2_hi - (x - (pio2_lo - x * r));
  }
  if (hx < 0) {
    const z = (1 + x) * 0.5;
    const s = Math.sqrt(z);
    const r = pOf(z) / qOf(z);
    const w = r * s - pio2_lo;
    return pi - 2.0 * (s + w);
  }
  const z = (1 - x) * 0.5;
  const s = Math.sqrt(z);
  const df = withLoZero(s);
  const c = (z - df * df) / (s + df);
  const r = pOf(z) / qOf(z);
  const w = r * s + c;
  return 2.0 * (df + w);
}

// ---- arctangent (s_atan.c, e_atan2.c) ---------------------------------------------

const atanhi = [4.63647609000806093515e-01, 7.85398163397448278999e-01, 9.82793723247329054082e-01, 1.57079632679489655800e+00];
const atanlo = [2.26987774529616870924e-17, 3.06161699786838301793e-17, 1.39033110312309984516e-17, 6.12323399573676603587e-17];
const aT = [
  3.33333333333329318027e-01, -1.99999999998764832476e-01, 1.42857142725034663711e-01,
  -1.11111104054623557880e-01, 9.09088713343650656196e-02, -7.69187620504482999495e-02,
  6.66107313738753120669e-02, -5.83357013379057348645e-02, 4.97687799461593236017e-02,
  -3.65315727442169155270e-02, 1.62858201153657823623e-02,
];

export function datan(x0: number): number {
  let x = x0;
  const hx = hi(x);
  const ix = hx & 0x7fffffff;
  if (ix >= 0x44100000) {   // |x| ≥ 2^66
    if (x !== x) return NaN;
    return hx > 0 ? atanhi[3] + atanlo[3] : -atanhi[3] - atanlo[3];
  }
  let id: number;
  if (ix < 0x3fdc0000) {   // |x| < 0.4375
    if (ix < 0x3e200000) return x;
    id = -1;
  } else {
    x = Math.abs(x);
    if (ix < 0x3ff30000) {
      if (ix < 0x3fe60000) { id = 0; x = (2.0 * x - 1.0) / (2.0 + x); }
      else { id = 1; x = (x - 1.0) / (x + 1.0); }
    } else if (ix < 0x40038000) { id = 2; x = (x - 1.5) / (1.0 + 1.5 * x); }
    else { id = 3; x = -1.0 / x; }
  }
  const z = x * x;
  const w = z * z;
  const s1 = z * (aT[0] + w * (aT[2] + w * (aT[4] + w * (aT[6] + w * (aT[8] + w * aT[10])))));
  const s2 = w * (aT[1] + w * (aT[3] + w * (aT[5] + w * (aT[7] + w * aT[9]))));
  if (id < 0) return x - x * (s1 + s2);
  const r = atanhi[id] - ((x * (s1 + s2) - atanlo[id]) - x);
  return hx < 0 ? -r : r;
}

const pi_o_2 = 1.5707963267948965580e+00;
const pi_o_4 = 7.8539816339744827900e-01;
const pi_lo = 1.2246467991473531772e-16;

export function datan2(y: number, x: number): number {
  if (x !== x || y !== y) return NaN;
  const hx = hi(x), ix = hx & 0x7fffffff, lx = lo(x);
  const hy = hi(y), iy = hy & 0x7fffffff, ly = lo(y);
  if (((hx - 0x3ff00000) | lx) === 0) return datan(y);   // x = 1
  const m = ((hy >>> 31) & 1) | ((hx >>> 30) & 2);
  if ((iy | ly) === 0) {   // y = ±0
    if (m <= 1) return y;
    return m === 2 ? pi : -pi;
  }
  if ((ix | lx) === 0) return hy < 0 ? -pi_o_2 : pi_o_2;   // x = ±0
  if (ix === 0x7ff00000) {   // x = ±inf
    if (iy === 0x7ff00000) {
      switch (m) { case 0: return pi_o_4; case 1: return -pi_o_4; case 2: return 3.0 * pi_o_4; default: return -3.0 * pi_o_4; }
    }
    switch (m) { case 0: return 0; case 1: return -0; case 2: return pi; default: return -pi; }
  }
  if (iy === 0x7ff00000) return hy < 0 ? -pi_o_2 : pi_o_2;   // y = ±inf
  const k = (iy - ix) >> 20;
  let z: number;
  if (k > 60) z = pi_o_2 + 0.5 * pi_lo;
  else if (hx < 0 && k < -60) z = 0.0;
  else z = datan(Math.abs(y / x));
  switch (m) {
    case 0: return z;
    case 1: return -z;
    case 2: return pi - (z - pi_lo);
    default: return (z - pi_lo) - pi;
  }
}

// ---- exponential and logarithm (e_exp.c, e_log.c) ---------------------------------

const o_threshold = 7.09782712893383973096e+02;
const u_threshold = -7.45133219101941108420e+02;
const ln2HI = [6.93147180369123816490e-01, -6.93147180369123816490e-01];
const ln2LO = [1.90821492927058770002e-10, -1.90821492927058770002e-10];
const invln2 = 1.44269504088896338700e+00;
const P1 = 1.66666666666666019037e-01;
const P2 = -2.77777777770155933842e-03;
const P3 = 6.61375632143793436117e-05;
const P4 = -1.65339022054652515390e-06;
const P5 = 4.13813679705723846039e-08;
const twom1000 = 9.33263618503218878990e-302;

export function dexp(x0: number): number {
  let x = x0;
  let hx = hi(x);
  const xsb = (hx >>> 31) & 1;
  hx &= 0x7fffffff;
  if (hx >= 0x40862e42) {   // |x| ≥ 709.78
    if (hx >= 0x7ff00000) {
      if (((hx & 0xfffff) | lo(x)) !== 0) return NaN;
      return xsb === 0 ? x : 0.0;
    }
    if (x > o_threshold) return Infinity;
    if (x < u_threshold) return 0;
  }
  let hiPart = 0, loPart = 0, k = 0;
  if (hx > 0x3fd62e42) {   // |x| > 0.5 ln2
    if (hx < 0x3ff0a2b2) {   // |x| < 1.5 ln2
      hiPart = x - ln2HI[xsb]; loPart = ln2LO[xsb]; k = 1 - xsb - xsb;
    } else {
      k = (invln2 * x + (xsb === 0 ? 0.5 : -0.5)) | 0;
      const t = k;
      hiPart = x - t * ln2HI[0];
      loPart = t * ln2LO[0];
    }
    x = hiPart - loPart;
  } else if (hx < 0x3e300000) {   // |x| < 2^-28
    return 1 + x;
  }
  const t = x * x;
  const c = x - t * (P1 + t * (P2 + t * (P3 + t * (P4 + t * P5))));
  if (k === 0) return 1 - ((x * c) / (c - 2.0) - x);
  const y = 1 - ((loPart - (x * c) / (2.0 - c)) - hiPart);
  if (k >= -1021) return withHi(y, hi(y) + (k << 20));
  return withHi(y, hi(y) + ((k + 1000) << 20)) * twom1000;
}

const ln2_hi = 6.93147180369123816490e-01;
const ln2_lo = 1.90821492927058770002e-10;
const two54 = 1.80143985094819840000e+16;
const Lg1 = 6.666666666666735130e-01;
const Lg2 = 3.999999999940941908e-01;
const Lg3 = 2.857142874366239149e-01;
const Lg4 = 2.222219843214978396e-01;
const Lg5 = 1.818357216161805012e-01;
const Lg6 = 1.531383769920937332e-01;
const Lg7 = 1.479819860511658591e-01;

export function dlog(x0: number): number {
  let x = x0;
  let hx = hi(x);
  const lx = lo(x);
  let k = 0;
  if (hx < 0x00100000) {
    if (((hx & 0x7fffffff) | lx) === 0) return -Infinity;
    if (hx < 0) return NaN;
    k -= 54;
    x *= two54;
    hx = hi(x);
  }
  if (hx >= 0x7ff00000) return x + x;
  k += (hx >> 20) - 1023;
  hx &= 0x000fffff;
  const i0 = (hx + 0x95f64) & 0x100000;
  x = withHi(x, hx | (i0 ^ 0x3ff00000));   // normalise x or x/2
  k += i0 >> 20;
  const f = x - 1.0;
  if ((0x000fffff & (2 + hx)) < 3) {   // |f| < 2^-20
    if (f === 0) return k === 0 ? 0 : k * ln2_hi + k * ln2_lo;
    const R = f * f * (0.5 - 0.33333333333333333 * f);
    if (k === 0) return f - R;
    return k * ln2_hi - ((R - k * ln2_lo) - f);
  }
  const s = f / (2.0 + f);
  const dk = k;
  const z = s * s;
  let i = hx - 0x6147a;
  const w = z * z;
  const j = 0x6b851 - hx;
  const t1 = w * (Lg2 + w * (Lg4 + w * Lg6));
  const t2 = z * (Lg1 + w * (Lg3 + w * (Lg5 + w * Lg7)));
  i |= j;
  const R = t2 + t1;
  if (i > 0) {
    const hfsq = 0.5 * f * f;
    if (k === 0) return f - (hfsq - s * (hfsq + R));
    return dk * ln2_hi - ((hfsq - (s * (hfsq + R) + dk * ln2_lo)) - f);
  }
  if (k === 0) return f - s * (f - R);
  return dk * ln2_hi - ((s * (f - R) - dk * ln2_lo) - f);
}

// ---- power ------------------------------------------------------------------------

/**
 * x^y: integer exponents (|y| ≤ 64) by repeated squaring — exact for the small powers
 * game logic uses; otherwise dexp(y·dlog(x)) (a few ulp; NaN for x < 0).
 */
export function dpow(x: number, y: number): number {
  if (y === 0) return 1;
  if (Number.isInteger(y) && Math.abs(y) <= 64) {
    let n = Math.abs(y);
    let base = x;
    let r = 1;
    while (n > 0) {
      if (n & 1) r *= base;
      base *= base;
      n >>= 1;
    }
    return y < 0 ? 1 / r : r;
  }
  if (x === 0) return y > 0 ? 0 : Infinity;
  if (x < 0) return NaN;
  if (x === 1) return 1;
  return dexp(y * dlog(x));
}

/**
 * √(a² + b² + …) from exact operations (`Math.hypot` is implementation-approximated
 * too). No overflow guard: game-logic magnitudes are small.
 */
export function dhypot(...v: number[]): number {
  let s = 0;
  for (const x of v) s += x * x;
  return Math.sqrt(s);
}
